import { createSelector } from 'reselect';

import { getRubiksContentList, getPageAllowedLists } from 'src/universal/redux/page';
import { getVacPageData } from 'page_modules/business-vac/redux/mainPageData';
import { getPznOfferList } from 'src/universal/redux/pznData';
import {
  isLegacyGcsCardMember,
  isGCSMember,
  getCardMemberCardsByPmc
} from 'src/universal/redux/cardMemberInfo.js';
import { getCardColorInfoList } from 'src/universal/redux/cardColors';
import { mapUrlWithDtmLinkQuery } from 'service_modules/models/dtm-tagging';

// utils
import { determineCardMemberCta } from 'src/universal/utils/orchestraUtils';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { formatBulletPoints } from './utils';
import { decideCardArtUrl } from 'src/universal/utils/cardColorChoices';
import isOfferFromPzn from 'src/universal/utils/isOfferFromPzn';
import {
  capitalizeButtonText,
  formatSpecialOfferButtonText
} from 'src/universal/utils/formatOfferButton';

const getCardTileContent = createSelector(
  [getVacPageData],
  ({ horizontalCardTiles }) => horizontalCardTiles
);

const getCardDetailContent = createSelector([getCardTileContent], ({ cards }) =>
  parseArrayToObject(cards, 'shortName')
);

const getPznOffersByShortName = createSelector(getPznOfferList, offerList =>
  parseArrayToObject(offerList, 'shortName')
);

const getCardsByShortName = createSelector(getVacPageData, ({ cards }) => {
  return parseArrayToObject(cards, 'shortName');
});

export const getCardTileDtm = createSelector([getCardTileContent], ({ dtmEvents }) =>
  parseArrayToObject(dtmEvents, 'id')
);

export const makeGetCardTitle = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => cardsByShortName[shortName]?.cardHeadline
  );

export const makeGetHighlights = () =>
  createSelector(
    [getCardDetailContent, (_, shortName) => shortName],
    (cardDetailContent, shortName) =>
      cardDetailContent[shortName]?.featuredBenefits.map(formatBulletPoints)
  );

export const makeGetLearnMoreUrl = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => {
      const learnMoreCta = cardsByShortName[shortName]?.learnMore || {};
      return mapUrlWithDtmLinkQuery(learnMoreCta.url, learnMoreCta.dtmTag);
    }
  );

export const makeGetLearnMoreDtm = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => cardsByShortName[shortName]?.learnMore?.dtmTag
  );

export const makeGetAnnualFee = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => cardsByShortName[shortName]?.annualFee
  );

export const makeGetAnnualFeeDisclaimer = () =>
  createSelector(
    [getRubiksContentList, (_, shortName) => shortName],
    (rubiksContent, shortName) =>
      rubiksContent.find(item => item.shortName === shortName)
        ?.annualFeeDisclaimer ?? ''
  );

export const makeGetIsTier2Offer = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) => {
      function isTier2Offer(offerButton) {
        const regex = /welcome|extra value/i;
        const offerButtonIncluesSpecial =
          offerButton.includes('special') || offerButton.includes('Special');
        const offerButtonContainsWelcomeOrExtraValue = regex.test(offerButton);
        return (
          offerButton !== '' &&
          (!offerButtonContainsWelcomeOrExtraValue || offerButtonIncluesSpecial)
        );
      }

      return isTier2Offer(pznOffersByShortName[shortName]?.offerButton ?? '');
    }
  );

export const makeGetIsTier1Offer = () =>
  createSelector(
    [getPageAllowedLists, (_, shortName) => shortName],
    ({ tier1Offers }, shortName) => tier1Offers.includes(shortName)
  );

export const makeGetOfferBadgeText = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) => {
      const offerButtonText = pznOffersByShortName[shortName]?.offerButton;
      return capitalizeButtonText(
        formatSpecialOfferButtonText(offerButtonText ?? '')
      );
    }
  );

export const makeGetDefaultTier1OfferBadgeText = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => {
      return cardsByShortName[shortName]?.defaultOfferButtonText;
    }
  );

export const makeGetDualOfferContentExists = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) =>
      (pznOffersByShortName[shortName]?.content?.length ?? 0) > 0
  );

export const makeGetIsPznOffer = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) => {
      const offer = pznOffersByShortName[shortName];
      return isOfferFromPzn(offer);
    }
  );

export const makeGetIsSpecialOffer = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) => {
      const offer = pznOffersByShortName[shortName];
      return offer?.offerButton?.indexOf('special') > -1;
    }
  );

export const makeGetOfferMetaData = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) => {
      const pznOffer = pznOffersByShortName?.[shortName];

      if (!pznOffer) {
        return null;
      }

      return {
        id: pznOffer.treatmentId,
        compId: pznOffer.componentId,
        iaCode: pznOffer.iaCode
      };
    }
  );

export const makeGetOfferContent = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) => {
      const offer = pznOffersByShortName[shortName];

      return {
        offerHeader: offer?.header,
        offerDescription: offer?.description,
        dualOfferHeader: offer?.content?.[0]?.header,
        dualOfferDescription: offer?.content?.[0]?.description
      };
    }
  );

export const makeGetCardArtUrl = () =>
  createSelector(
    [getCardsByShortName, getCardColorInfoList, (_, shortName) => shortName],
    (cardsByShortName, colorInfoList, shortName) => {
      const colorInfo = colorInfoList.find(color => color.shortName === shortName);
      const cardArt = cardsByShortName[shortName]?.appAssets?.cardArt;
      return decideCardArtUrl(cardArt, colorInfo);
    }
  );

export const makeGetUpgradeCtaData = () =>
  createSelector(
    [
      getCardsByShortName,
      isLegacyGcsCardMember,
      isGCSMember,
      (_, shortName) => shortName
    ],
    (cardsByShortName, isLegacyGcs, isGcs, shortName) => {
      return determineCardMemberCta(
        cardsByShortName[shortName],
        isGcs || isLegacyGcs
      );
    }
  );

export const makeGetQuickViewItems = () =>
  createSelector(
    [getCardDetailContent, (_, shortName) => shortName],
    (cardDetailContent, shortName) => cardDetailContent[shortName]?.quickViewItems
  );

export const makeGetFeaturedBenefits = () =>
  createSelector(
    [getCardDetailContent, (_, shortName) => shortName],
    (cardDetailContent, shortName) => cardDetailContent[shortName]?.featuredBenefits
  );

export const makeGetRate = () =>
  createSelector(
    [getPznOffersByShortName, (_, shortName) => shortName],
    (pznOffersByShortName, shortName) => pznOffersByShortName[shortName]?.rate
  );

export const makeGetInterstitialToApp = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) =>
      cardsByShortName[shortName]?.interstitialToApp ?? false
  );

export const makeGetApplyNowOnClick = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) =>
      cardsByShortName[shortName]?.applyButton?.onClick ?? (() => {})
  );

export const makeGetApplyNowUrl = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => {
      const applyNowLink = cardsByShortName[shortName]?.applyButton?.url ?? '';
      return applyNowLink;
    }
  );

export const makeGetApplyNowId = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => {
      const applyNowLink = cardsByShortName[shortName]?.applyButton?.id ?? undefined;
      return applyNowLink;
    }
  );

export const makeGetApplyNowDtm = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) =>
      cardsByShortName[shortName]?.applyButton?.dtmTag ?? {}
  );

export const makeGetTermLinks = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => cardsByShortName[shortName]?.termLinks
  );

export const makeGetIsCardMemberCard = () =>
  createSelector(
    [getCardsByShortName, getCardMemberCardsByPmc, (_, shortName) => shortName],
    (cardsByShortName, cardMemberCardsByPmc, shortName) => {
      const { pmcCode } = cardsByShortName?.[shortName] ?? {};
      return !!cardMemberCardsByPmc[pmcCode];
    }
  );

export const makeGetAddAnEmployeeUrl = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => cardsByShortName[shortName]?.addSupUser?.url
  );

export const makeGetAddAnEmployeeDtm = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) => cardsByShortName[shortName]?.addSupUser?.dtm
  );

export const makeGetAddAnEmployeeText = () =>
  createSelector([getCardTileContent], ({ buttons }) => {
    return parseArrayToObject(buttons, 'id')?.addAnEmployee?.text;
  });

export const makeGetViewMoreButton = () =>
  createSelector([getCardTileContent], ({ buttons }) => {
    return parseArrayToObject(buttons, 'id')?.viewMoreLearnMore;
  });

export const makeGetAddToCompareText = () =>
  createSelector([getVacPageData], ({ addToCompare }) => addToCompare?.title);

export const makeGetCardFeatureHeader = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) =>
      cardsByShortName[shortName]?.cardFeatures?.header
  );

export const makeGetCardFeatureDescription = () =>
  createSelector(
    [getCardsByShortName, (_, shortName) => shortName],
    (cardsByShortName, shortName) =>
      cardsByShortName[shortName]?.features?.descriptionList?.[0]
  );
